import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDealer } from '../../context/DealerContext';
import { dealerApi, CreditPack, CreditRequest } from '../../services/dealerApi';
import ProformaAcceptDialog from '../../components/dealer/ProformaAcceptDialog';
import CommitSpendNote from '../../components/dealer/CommitSpendNote';
import Money from '../../components/dealer/Money';
import { dealerErrorMessage, fmt } from '../../utils/dealer';

/**
 * Spec 085 (Flow C) — buy a credit pack: active packs at max(pack, dealer) discount (BR-12) → pack order
 * (proforma) → accept. Ordering and accepting are money commitments (owner or canCommitSpend, BR-6).
 */
export default function BuyPackPage() {
  const { t } = useLanguage();
  const { token, canCommitSpend } = useDealer();
  const [packs, setPacks] = useState<CreditPack[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ordering, setOrdering] = useState<string | null>(null);
  const [order, setOrder] = useState<CreditRequest | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) return;
    dealerApi.packs(token).then(setPacks).catch((err) => setError(dealerErrorMessage(err, t)));
  }, [token, t]);

  const orderPack = async (pack: CreditPack) => {
    if (!token) return;
    setOrdering(pack.id);
    setError(null);
    try {
      const r = await dealerApi.orderPack(pack.id, token);
      setOrder(r);
      setAccepting(true);
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    } finally {
      setOrdering(null);
    }
  };

  return (
    <div className="dealer-page">
      <div className="dealer-page__header">
        <div>
          <h2>{t('dealer.packs.title')}</h2>
          <p className="dealer-muted">{t('dealer.packs.subtitle')}</p>
        </div>
      </div>
      {!canCommitSpend && <CommitSpendNote />}
      {error && <div className="error-message">{error}</div>}
      {order && !accepting && (
        <div className="dealer-card dealer-success" role="status">
          <h3>{fmt(t('dealer.request.createdTitle'), { number: order.number })}</h3>
          <p>{order.status === 'AWAITING_PAYMENT'
            ? fmt(t('dealer.request.awaitingPayment'), { number: order.number })
            : t('dealer.request.createdBody')}</p>
          <div className="dealer-actions">
            {order.status === 'PROFORMA_ISSUED' && (
              <button type="button" className="btn btn-primary" disabled={!canCommitSpend} onClick={() => setAccepting(true)}>
                {t('dealer.proforma.accept')}
              </button>
            )}
            <Link className="btn btn-secondary" to="/dealer/credit?tab=requests">{t('dealer.request.viewRequests')}</Link>
          </div>
        </div>
      )}
      {packs && packs.length === 0 && <p className="dealer-muted dealer-empty-row">{t('dealer.packs.empty')}</p>}
      {packs && packs.length > 0 && (
        <div className="dealer-pack-grid">
          {packs.map((p) => (
            <div key={p.id} className="dealer-card dealer-pack">
              <h3>{p.name}</h3>
              <dl className="dealer-dl">
                <dt>{t('dealer.quote.faceValue')}</dt><dd><Money value={p.faceValueAed} /></dd>
                <dt>{t('dealer.quote.discount')}</dt><dd dir="ltr">{p.appliedDiscountPct ?? p.discountPct}%</dd>
                <dt className="dealer-dl__total">{t('dealer.quote.payable')}</dt>
                <dd className="dealer-dl__total"><Money value={p.payableAed ?? null} /></dd>
              </dl>
              <p className="dealer-muted">{t('dealer.packs.expiryNote')}</p>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canCommitSpend || ordering !== null}
                title={canCommitSpend ? undefined : t('dealer.commit.disabledReason')}
                onClick={() => orderPack(p)}
              >
                {ordering === p.id ? t('dealer.common.saving') : t('dealer.packs.order')}
              </button>
            </div>
          ))}
        </div>
      )}
      {accepting && order && token && (
        <ProformaAcceptDialog
          token={token}
          request={order}
          onClose={() => setAccepting(false)}
          onAccepted={(r) => { setAccepting(false); setOrder(r); }}
        />
      )}
    </div>
  );
}
